/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import {
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import SelectorOptionButton from 'components/SelectorOptionButton/SelectorOptionButton';
import PageTitle from 'components/PageTitle';
import i18n from 'i18n';
import { GetAllServersRequestProps } from 'modules/environments/requestInterfaces';
import {
  EnvironmentFields,
  ServerFields,
} from 'modules/environments/interfaces';
import { useParams } from 'react-router';
import {
  getAllServersRequest,
  unblockServerActionsRequest,
} from 'modules/environments/actions';
import { Alert } from '@material-ui/lab';
import { formatHourMinute } from 'utils/helpers';
import { isClientOperator } from 'utils/role';
import Button from 'components/Button';
import { ModalFields } from 'modules/modal/interfaces';
import { Formik } from 'formik';
import { modalClose, modalOpen } from 'modules/modal/actions';
import GenericForm from 'skyone-lib/lib/GenericForm';
import palette from 'theme/palette';
import styles from './styles';
import OptimizedPublication from './OptimizedPublication';
import DefaultPublication from './DefaultPublication';
import KeepPublication from './KeepPublication';
import ReconnectionPublication from './ReconnectionPublication';
import SchedulePublication from './SchedulePublication';
import { unblockSchema } from '../ListServers/PreparationServer/schema';
import { overrideModalBlockActions } from '../ListServers/styles';

const useStyles = makeStyles(styles);

const requiredPublicationSchedule: Array<keyof PermissionsData> = [
  'list_image_publication_schedule',
  'create_image_publication_schedule',
  'cancel_image_publication_schedule',
  'publish_image',
  'publish_image_v2',
];

const unblockServerInitialValues = {
  unblock_reason: '',
};

interface Props {
  permissions?: PermissionsData;
  preparationServer: ServerFields;
  isLoadingPreparationServer: boolean;
  environmentSelected: EnvironmentFields | null;
  redirectToTab: string;
  highlightSection?: string;
  role?: RolesEnum;
  getAllServersRequest: (props: GetAllServersRequestProps) => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
  setHighlightSection: React.Dispatch<React.SetStateAction<string>>;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  unblockServerActionsRequest: (props) => void;
}

const Publications = ({
  permissions,
  preparationServer,
  isLoadingPreparationServer,
  environmentSelected,
  redirectToTab,
  highlightSection,
  role,
  getAllServersRequest,
  setCurrentTab,
  setHighlightSection,
  modalClose,
  modalOpen,
  unblockServerActionsRequest,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const [publicationLimitReached, setPublicationLimitReached] = React.useState(
    false
  );
  const [
    publicationLimitReachedDaily,
    setPublicationLimitReachedDaily,
  ] = React.useState(false);

  React.useEffect(() => {
    if (
      environmentSelected &&
      environmentSelected?.behavior?.type !== 'manual'
    ) {
      setPublicationLimitReached(
        environmentSelected?.publication
          ?.executed_instance_blocking_publications >=
          environmentSelected?.publication?.limit_instance_blocking_publications
      );
      if (
        environmentSelected?.publication
          ?.limit_instance_blocking_publications_daily !== null
      ) {
        setPublicationLimitReachedDaily(
          environmentSelected?.publication
            ?.executed_instance_blocking_publications_daily >=
            Number(
              environmentSelected?.publication
                ?.limit_instance_blocking_publications_daily
            )
        );
      }
    } else {
      setPublicationLimitReached(false);
      setPublicationLimitReachedDaily(false);
    }
  }, [environmentSelected]);

  const getPreparationServer = (getFromProvider = false) => {
    getAllServersRequest({
      id,
      force_refresh: getFromProvider,
      type: 'preparation',
    });
  };

  React.useEffect(() => {
    getPreparationServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [publicationComponent, setPublicationComponent] = React.useState<
    'optimized' | 'default' | 'keep' | 'reconnection' | 'schedule' | string
  >(redirectToTab || (permissions?.publish_image_v2 ? 'optimized' : 'default'));

  const publishOptions = [
    {
      name: 'optimized',
      labelKey: 'OPTIMIZED_KEY',
      isNew: true,
      hidden: !permissions?.publish_image_v2,
    },
    {
      name: 'default',
      labelKey: 'DEFAULT_KEY',
      hidden: !permissions?.publish_image,
    },
    {
      name: 'keep',
      labelKey: 'ENVIRONMENTS_PAGE.KEEP_USERS_CONNECTED_KEY',
      hidden:
        publicationLimitReached ||
        publicationLimitReachedDaily ||
        !environmentSelected?.publication
          ?.publication_can_block_old_instances ||
        !permissions?.publish_image,
    },
    {
      name: 'reconnection',
      labelKey: 'ENVIRONMENTS_PAGE.REQUEST_USER_FEATURE_KEY',
      hidden: !permissions?.publish_image,
    },
    {
      name: 'schedule',
      labelKey: 'ENVIRONMENTS_PAGE.SCHEDULE_PUBLISH_KEY',
      hidden:
        !!permissions &&
        requiredPublicationSchedule.every(
          permission => !permissions[permission]
        ),
    },
  ].filter(option => !option?.hidden);

  const renderPublishComponent = () => {
    switch (publicationComponent) {
      case 'optimized':
        return (
          <OptimizedPublication
            setCurrentTab={setCurrentTab}
            preparationServer={preparationServer}
            highlightSection={highlightSection}
          />
        );
      case 'default':
        return (
          <DefaultPublication
            setCurrentTab={setCurrentTab}
            preparationServer={preparationServer}
            highlightSection={highlightSection}
          />
        );
      case 'keep':
        return (
          <KeepPublication
            setCurrentTab={setCurrentTab}
            preparationServer={preparationServer}
            highlightSection={highlightSection}
          />
        );
      case 'reconnection':
        return (
          <ReconnectionPublication
            setCurrentTab={setCurrentTab}
            preparationServer={preparationServer}
            highlightSection={highlightSection}
          />
        );
      case 'schedule':
        return (
          <SchedulePublication
            setCurrentTab={setCurrentTab}
            highlightSection={highlightSection}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmitUnblockServer = values => {
    unblockServerActionsRequest({
      environmentUuid: id,
      instanceId: preparationServer.instance_id,
      values,
    });
    modalClose();
  };

  const isActionsBlockedForOperator =
    preparationServer?.block_actions_enabled &&
    !preparationServer?.can_execute_actions_on_blocked_server;

  const handleUnblockActionsServerPreparation = () =>
    modalOpen({
      title: i18n.t('UNBLOCK_ACTIONS_SERVER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid container spacing={2}>
          <Grid item>
            <Typography className={classes.alertText}>
              {i18n.t('UNBLOCKING_SERVER_ACTIONS_RESPONSIBLE_KEY', {
                name: `${preparationServer?.blocked_by?.first_name} ${preparationServer?.blocked_by?.last_name} (${preparationServer?.blocked_by?.email})`,
                time: formatHourMinute(preparationServer?.blocked_at),
                unblockDate: formatHourMinute(preparationServer?.unblock_time),
              })}
            </Typography>
            <Typography className={classes.alertText} component="div">
              <Box color={palette.blue} fontWeight="bold" display="inline">
                {`${i18n.t('BLOCKING_REASON_KEY')}: `}
              </Box>
              {preparationServer?.block_reason}
            </Typography>
            <Typography className={classes.alertText}>
              {i18n.t('UNBLOCKING_SERVER_ACTIONS_DESCRIPTION_KEY')}
            </Typography>
          </Grid>
          <Formik
            initialValues={unblockServerInitialValues}
            onSubmit={handleSubmitUnblockServer}
            enableReinitialize
            validationSchema={unblockSchema}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid item container xs={12} spacing={2}>
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('UNBLOCKING_REASON_KEY'),
                      stateKey: 'unblock_reason',
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <Grid
                  container
                  justify="flex-end"
                  className={classes.buttonsBlockServerActions}
                >
                  <Button id="submit-button" submitButton onClick={submitForm}>
                    {i18n.t('UNBLOCK_KEY')}
                  </Button>
                  <Button
                    id="cancel-button"
                    variant="outlined"
                    onClick={modalClose}
                  >
                    {i18n.t('CANCEL_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      ),
    });

  return (
    <>
      <Grid container className={classes.header}>
        <Grid item className={classes.actionsGrid}>
          <PageTitle
            title={i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_NEW_VERSION_KEY')}
            subtitle={i18n.t('PUBLICATIONS_PAGE_TITLE_KEY')}
          />
        </Grid>
      </Grid>
      {isLoadingPreparationServer ? (
        <Grid container justify="center" className={classes.loadingContainer}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {preparationServer?.instance_id &&
          (!preparationServer?.block_actions_enabled ||
            (preparationServer?.block_actions_enabled &&
              preparationServer?.can_execute_actions_on_blocked_server)) ? (
            <>
              <Grid
                container
                justify="center"
                className={classes.selectorTextGrid}
              >
                <Typography className={classes.selectorText}>
                  {`${i18n.t('SELECT_PUBLICATION_MODE_KEY')}:`}
                </Typography>
              </Grid>
              <Grid container justify="center">
                {publishOptions?.map(option => (
                  <SelectorOptionButton
                    key={option?.name}
                    name={option?.name}
                    labelKey={option?.labelKey}
                    selectedItem={publicationComponent}
                    onChange={() => {
                      setPublicationComponent(option?.name);
                      setHighlightSection('');
                    }}
                    newTag={option?.isNew}
                  />
                ))}
              </Grid>
              <Grid container>{renderPublishComponent()}</Grid>
            </>
          ) : (
            <Grid container justify="center">
              <Alert className={classes.alert} severity="warning" color="info">
                <Grid item>
                  {isActionsBlockedForOperator &&
                  preparationServer?.instance_id ? (
                    <Grid item>
                      {!isClientOperator(role) && (
                        <>
                          <Typography className={classes.alertText}>
                            {i18n.t('BLOCKED_PUBLICATIONS_DESCRIPTION_KEY', {
                              name: `${preparationServer?.blocked_by?.first_name} ${preparationServer?.blocked_by?.last_name} (${preparationServer?.blocked_by?.email})`,
                              time: formatHourMinute(
                                preparationServer?.blocked_at
                              ),
                            })}
                          </Typography>
                          <Typography
                            className={classes.alertText}
                            component="div"
                          >
                            <Box
                              color={palette.blue}
                              fontWeight="bold"
                              display="inline"
                            >
                              {`${i18n.t('BLOCKING_REASON_KEY')}: `}
                            </Box>
                            {preparationServer?.block_reason}
                          </Typography>
                        </>
                      )}
                      <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                      >
                        <Grid item>
                          {!isClientOperator(role) ? (
                            <Typography className={classes.alertText}>
                              {i18n.t('BLOCKED_PUBLICATIONS_RELEASE_KEY', {
                                unblockDate: formatHourMinute(
                                  preparationServer?.unblock_time
                                ),
                              })}
                            </Typography>
                          ) : (
                            <Typography className={classes.alertText}>
                              {i18n.t(
                                'SERVER_MAINTENANCE_ACTIONS_RELEASE_KEY',
                                {
                                  unblockDate: formatHourMinute(
                                    preparationServer?.unblock_time
                                  ),
                                }
                              )}
                            </Typography>
                          )}
                        </Grid>
                        {permissions?.can_unblock_actions_preparation_server ? (
                          <Button
                            variant="outlined"
                            outlineBlue
                            onClick={handleUnblockActionsServerPreparation}
                          >
                            {i18n.t('UNBLOCK_KEY')}
                          </Button>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography className={classes.alertText}>
                      {i18n.t('RULE_TO_PUBLISH_KEY')}
                    </Typography>
                  )}
                </Grid>
              </Alert>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: {
    preparationServer,
    isLoadingPreparationServer,
    environmentSelected,
  },
}: ApplicationState) => ({
  permissions: data?.permissions,
  preparationServer,
  isLoadingPreparationServer,
  environmentSelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getAllServersRequest,
  modalClose,
  modalOpen,
  unblockServerActionsRequest,
})(Publications);
